import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@startupquickstart/frontend';
import {
  ArrowLeft,
  Bug,
  Database,
  HelpCircle,
  PencilLine,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import type { CreateTicketInput } from '@/api/support';
import { TicketForm } from './TicketForm';

type TicketType = CreateTicketInput['type'];

const TYPE_OPTIONS: Array<{
  type: TicketType;
  title: string;
  blurb: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  {
    type: 'bug',
    title: 'Bug',
    blurb: 'Something is broken or not behaving the way it should.',
    icon: Bug,
  },
  {
    type: 'feature',
    title: 'Feature request',
    blurb: 'I wish the app could…',
    icon: Sparkles,
  },
  {
    type: 'content-fix',
    title: 'Content fix',
    blurb: "An attraction, tour, or hotel's content needs correcting.",
    icon: PencilLine,
  },
  {
    type: 'question',
    title: 'Question',
    blurb: "How do I…? I'm stuck on something.",
    icon: HelpCircle,
  },
  {
    type: 'data-quality',
    title: 'Data quality',
    blurb: 'A specific field is wrong or missing on a record.',
    icon: Database,
  },
];

interface NewTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * If set, opens directly into the matching type's form instead of the
   * type picker. The Back arrow on the dialog title still lets the user
   * change type. Used by the global Support button which assumes the
   * common case is "report a bug from this page".
   */
  defaultType?: TicketType;
  /**
   * Pre-fill the bug form's URL field with this value. Only meaningful
   * when `defaultType === 'bug'`. Pass an empty string to suppress the
   * auto-captured URL (e.g. when opened from the support page itself).
   */
  initialUrl?: string;
}

/**
 * Modal-driven create-ticket flow. Two phases:
 *  1. Type picker — a 2-3 column grid of cards.
 *  2. Type-specific form — `<TicketForm>` rendered with the chosen type.
 *
 * Both phases live inside one `<Dialog>` so the user keeps their place in
 * the underlying ticket list. On successful submit the dialog closes and
 * the list re-queries via the existing `useCreateTicket` invalidation.
 */
export function NewTicketDialog({
  open,
  onOpenChange,
  defaultType,
  initialUrl,
}: NewTicketDialogProps) {
  const [selectedType, setSelectedType] = useState<TicketType | null>(
    defaultType ?? null,
  );

  // Reset to the dialog's default phase whenever it closes, so the next
  // open shows the right starting view (picker, or pre-selected type).
  function handleOpenChange(next: boolean): void {
    if (!next) setSelectedType(defaultType ?? null);
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {selectedType && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-ml-2"
                onClick={() => setSelectedType(null)}
                aria-label="Back to type picker"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {selectedType
              ? TYPE_OPTIONS.find((o) => o.type === selectedType)?.title
              : 'New ticket'}
          </DialogTitle>
          {!selectedType && (
            <DialogDescription>
              Pick the kind of ticket you want to file. Each type has a
              short, focused form so you don't have to think about what to
              fill in.
            </DialogDescription>
          )}
        </DialogHeader>

        {selectedType ? (
          <TicketForm
            type={selectedType}
            initialUrl={selectedType === 'bug' ? initialUrl : undefined}
            onSuccess={() => handleOpenChange(false)}
            onCancel={() => handleOpenChange(false)}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {TYPE_OPTIONS.map(({ type, title, blurb, icon: Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => setSelectedType(type)}
                className={cn(
                  'rounded-xl border bg-card p-4 text-left shadow-sm',
                  'transition-colors hover:border-primary hover:bg-muted/30 focus:outline-none focus:ring-2 focus:ring-ring',
                )}
              >
                <Icon className="h-5 w-5 text-primary" />
                <h3 className="mt-2 text-base font-medium">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{blurb}</p>
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
